import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Alert, Container, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { Action } from '@reduxjs/toolkit';
import {
  ActionButtons,
  addStatusToast,
  DeleteConfirmation,
  Loader,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LOADING_STATUS, LOCALES } from '../../common/constants';
import { initialTranslationsState } from '../../common/types/translations.types';
import {
  deleteRegionAsync,
  getRegionDetailsAsync,
  RegionDetails,
  saveRegionAsync,
  selectRegionDetails,
  selectRegionDetailsLoadingStatus,
  updateRegionAsync,
} from './regionsSlice';
import {
  getCountriesAsync,
  selectCountryItems,
} from '../countries/countriesSlice';
import { clearInputErrors, selectInputErrors } from '../errors/errorsSlice';
import InputErrorsDisplay from '../errors/InputErrorsDisplay';
import { ensureTranslationItems } from '../../common/utils/translationUtils';

function RegionAddOrUpdate() {
  const backLinkUrl = '/locations/regions';
  const params = useParams();
  const dispatch = useAppDispatch();
  const regionDetails = useAppSelector(selectRegionDetails);
  const countries = useAppSelector(selectCountryItems);
  const regionDetailsLoadingStatus = useAppSelector(
    selectRegionDetailsLoadingStatus,
  );
  const inputErrors = useAppSelector(selectInputErrors);
  const isAddMode = !params.regionId;

  const initialRegionState: RegionDetails = {
    nameTranslations: initialTranslationsState,
    descriptionTranslations: initialTranslationsState,
    metaTitleTranslations: ensureTranslationItems([], 'RegionMetaTitle'),
    metaDescriptionTranslations: ensureTranslationItems(
      [],
      'RegionMetaDescription',
    ),
    countryId: '',
  };

  const [region, setRegion] = useState(initialRegionState);
  const [regionNameFi, setRegionNameFi] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleted, setDeleted] = useState(false);

  const countryOptions = countries.map(country => {
    return { value: country.id, label: country.name };
  });
  const initFetch = useCallback(() => {
    dispatch(getRegionDetailsAsync(params.regionId));
    dispatch(
      getCountriesAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
        culture: LOCALES.Fi,
      }),
    );
  }, [dispatch, params.regionId]);

  useEffect(() => {
    dispatch(clearInputErrors());
  }, [dispatch]);

  useEffect(() => {
    if (!isAddMode) {
      initFetch();
    }
  }, [isAddMode, initFetch]);

  useEffect(() => {
    if (!isAddMode) {
      setRegion({
        ...regionDetails,
        metaTitleTranslations: ensureTranslationItems(
          regionDetails.metaTitleTranslations,
          'RegionMetaTitle',
        ),
        metaDescriptionTranslations: ensureTranslationItems(
          regionDetails.metaDescriptionTranslations,
          'RegionMetaDescription',
        ),
      });
    }
  }, [isAddMode, regionDetails]);

  useEffect(() => {
    if (region.nameTranslations.length > 0) {
      const regionObject = region.nameTranslations.find(
        obj => obj.locale === 'fi_FI',
      );
      setRegionNameFi(regionObject.text);
    }
  }, [region]);

  const deleteRegion = () => {
    dispatch(deleteRegionAsync(params.regionId));
    setDeleted(true);
    setShowDeleteModal(false);
  };

  const handleTranslation = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const { culture } = event.target.dataset;
    const textValue = event.target.value;
    const newRegion = { ...region };

    newRegion[finalProp] = region[finalProp].map(item => {
      if (item.locale === culture) {
        return { ...item, text: textValue };
      }
      return item;
    });
    setRegion(newRegion);
  };

  const handleSelectChange = (selectedOption, name) => {
    const newRegion = { ...region };
    newRegion[name] = selectedOption.value;
    setRegion(newRegion);
  };

  const addOrUpdateRegion = async () => {
    let actionResult: Action<string>;
    if (isAddMode) {
      actionResult = await dispatch(saveRegionAsync(region));
    } else {
      actionResult = await dispatch(updateRegionAsync(region));
    }

    if (
      saveRegionAsync.fulfilled.match(actionResult) ||
      updateRegionAsync.fulfilled.match(actionResult)
    ) {
      dispatch(
        addStatusToast({
          Message: `Maan ${regionNameFi} tiedot on tallennettu.`,
        }),
      );
      setSubmitted(true);
      dispatch(clearInputErrors());
    }
  };

  const generatePageTitle = function () {
    if (isAddMode) {
      return 'Uuden alueen lisäys';
    }
    if (
      regionDetailsLoadingStatus === LOADING_STATUS.IDLE &&
      regionNameFi !== ''
    ) {
      return `Alueen tietojen päivitys: ${regionNameFi}`;
    }
    return 'Alueen tietojen päivitys';
  };

  return (
    <>
      <Helmet>
        <title>Sijaintitiedot - Alueet</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={backLinkUrl}
          Breadcrumbs={['Sijaintitiedot', 'Alueet']}
          Title={generatePageTitle()}
        />

        <Loader
          IsLoading={regionDetailsLoadingStatus === LOADING_STATUS.LOADING}
        />

        {regionDetailsLoadingStatus === LOADING_STATUS.IDLE && (
          <Container className="container-form mt-48">
            {region.nameTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="nameTranslations"
                className="mb-40"
              >
                <Form.Label>Nimi ({item.locale})</Form.Label>
                <Form.Control
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="nameTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            <Form.Group className="mb-40">
              <Form.Label>Maa</Form.Label>
              <Select
                inputId="countryId"
                name="countryId"
                placeholder="Valitse maa"
                isSearchable
                options={countryOptions}
                onChange={option => handleSelectChange(option, 'countryId')}
                value={countryOptions.filter(
                  option => option.value === region.countryId,
                )}
                classNames={{
                  control: state => 'form-control form-control-react-select',
                }}
              />
            </Form.Group>

            <hr className="hr-light mb-32" />

            {region.descriptionTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="descriptionTranslations"
                className="mb-40"
              >
                <Form.Label>Kuvaus ({item.locale})</Form.Label>
                <Form.Control
                  as="textarea"
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="descriptionTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            {region.metaTitleTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="metaTitleTranslations"
                className="mb-40"
              >
                <Form.Label>Meta-otsikko ({item.locale})</Form.Label>
                <Form.Control
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="metaTitleTranslations"
                />
              </Form.Group>
            ))}

            <hr className="hr-light mb-32" />

            {region.metaDescriptionTranslations?.map(item => (
              <Form.Group
                key={`${item.keyId}-${item.locale}`}
                controlId="metaDescriptionTranslations"
                className="mb-40"
              >
                <Form.Label>Meta-kuvausteksti ({item.locale})</Form.Label>
                <Form.Control
                  as="textarea"
                  autoComplete="off"
                  data-culture={item.locale}
                  value={item.text}
                  onChange={handleTranslation}
                  name="metaDescriptionTranslations"
                />
              </Form.Group>
            ))}

            <InputErrorsDisplay inputErrors={inputErrors} />

            <ActionButtons
              CancelButtonText={
                deleted || submitted ? 'Palaa takaisin' : 'Peruuta'
              }
              CancelButtonUrl={backLinkUrl}
              DeleteButtonText={isAddMode || deleted ? '' : 'Poista alue'}
              HandleDelete={
                isAddMode || deleted ? null : () => setShowDeleteModal(true)
              }
              HandleSave={addOrUpdateRegion}
            />

            {submitted && <Alert variant="success">Tiedot tallennettu!</Alert>}
            {(region.isDeleted || deleted) && (
              <Alert variant="danger">Alue on poistettu!</Alert>
            )}
          </Container>
        )}
      </Container>

      <DeleteConfirmation
        ConfirmDelete={deleteRegion}
        HideModal={() => setShowDeleteModal(false)}
        Id={region.id}
        Message={`Haluatko varmasti poistaa alueen ${regionNameFi}?`}
        ShowModal={showDeleteModal}
      />
    </>
  );
}

export default RegionAddOrUpdate;
